import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { bulanDari, periodeSah, pilihanPeriode } from '../lib/periode.js';

/**
 * Konteks PERIODE — satu bulan yang dipilih di bilah atas (`5:58`) dan dibaca layar.
 *
 * Ia di `state/` dan bukan di dalam `Shell`, karena pemakainya ada di dua tempat yang
 * berjauhan: pemilihnya di bilah atas, pembacanya di dalam layar. Konteks React adalah
 * satu-satunya bentuk yang menghubungkan keduanya tanpa menurunkan prop lewat seluruh
 * pohon — dan tanpa membuat setiap layar menerima prop yang dua puluh di antaranya
 * abaikan.
 *
 * Ia TIDAK disimpan di `localStorage`. Alasannya operasional: seorang manajer yang
 * membuka konsol esok pagi mengharapkan bulan berjalan, bukan bulan yang ia periksa
 * kemarin sore — dan sebuah dasbor yang diam-diam menampilkan bulan lalu adalah bentuk
 * paling mahal dari angka yang salah.
 */
export interface PeriodeState {
  /** `YYYY-MM`. */
  periode: string;
  setPeriode: (nilai: string) => void;
  /** Dua belas bulan ke belakang, terbaru lebih dulu. */
  pilihan: string[];
}

const PeriodeContext = createContext<PeriodeState | null>(null);

export function PeriodeProvider({
  children,
  awal,
}: {
  children: ReactNode;
  /** Hanya untuk uji — produksi selalu mulai dari bulan berjalan. */
  awal?: string;
}) {
  const [periode, setPeriodeState] = useState<string>(() =>
    awal && periodeSah(awal) ? awal : bulanDari(new Date()),
  );
  const pilihan = useMemo(() => pilihanPeriode(), []);
  const setPeriode = useCallback((nilai: string) => {
    if (periodeSah(nilai)) setPeriodeState(nilai);
  }, []);
  const value = useMemo<PeriodeState>(
    () => ({ periode, setPeriode, pilihan }),
    [periode, setPeriode, pilihan],
  );
  return <PeriodeContext.Provider value={value}>{children}</PeriodeContext.Provider>;
}

/**
 * Periode yang sedang dipilih.
 *
 * Ia TIDAK melempar ketika penyedianya tidak ada: layar dapat dirender di luar kerangka
 * (uji, dan `/privacy-notice` yang dilayani sebelum sesi ada), dan bulan berjalan adalah
 * jawaban yang benar di sana.
 */
export function usePeriode(): PeriodeState {
  const ctx = useContext(PeriodeContext);
  const cadangan = useMemo<PeriodeState>(
    () => ({ periode: bulanDari(new Date()), setPeriode: () => {}, pilihan: pilihanPeriode() }),
    [],
  );
  return ctx ?? cadangan;
}
